import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { JSEncrypt } from "jsencrypt";
import Label from "../common/Label";
import Input from "../common/Input";
import Button from "../common/Button";
import { BsEye, BsEyeSlash, Spinner } from "../../icons/CommonIcons";
import { nodalOfficersRoleList } from "../../config/roleConfig";
import api from "../../api/ApiService";
import { useLanguage } from "../LanguageProvider/LanguageProvider";
import { useAuth } from "./AuthProvider";
import { scriptRegex } from "../../utils/utils";

const AdminLoginForm = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [usernameError, setUsernameError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [publicKey, setPublicKey] = useState("");
  const passwordRef = useRef(""); // 🔒 store password in memory only
  const { setTokenGenerated, setUserType, setRoleId } = useAuth();
  const { setLanguage } = useLanguage();
  const [passwordVisible, setPasswordVisile] = useState(false);

  useEffect(() => {
    setLanguage(false);
    fetchPublicKey();
  }, []);

  const fetchPublicKey = async () => {
    try {
      const res = await api.get("/public-key");
      setPublicKey(res.key);
    } catch (err) {
      console.error("Failed to fetch public key", err);
    }
  };

  const encryptPassword = (plainPassword) => {
    const encrypt = new JSEncrypt();
    encrypt.setPublicKey(publicKey);
    return encrypt.encrypt(plainPassword);
  };

  const handleSubmit = async () => {
    try {
      setUsernameError("");
      setPasswordError("");

      if (!username) setUsernameError("Username is required");
      if (!passwordRef.current) setPasswordError("Password is required");

      if (!username || !passwordRef.current) return;

      setLoggingIn(true);
      const encryptedPassword = encryptPassword(passwordRef.current);

      await api.initCsrf();

      const res = await api.post("/login", {
        mobile_number: username,
        password: encryptedPassword,
      });

      if (res.status !== 200) {
        setPasswordError("Login Failed");
        return;
      }
      passwordRef.current = "";

      const userDetails = res?.user;
      if (!userDetails) {
        setPasswordError("User details not found");
        return;
      }

      setUsername("");
      setUsernameError("");
      setPasswordError("");

      setUserType(userDetails?.role);
      setTokenGenerated(true);
      setRoleId(userDetails?.role_id);

      const redirectPath = userDetails?.role_id
        ? nodalOfficersRoleList.find(
            (r) => r.value === String(userDetails?.role_id),
          )?.defaultPath
        : "/admin/dashboard";

      setTimeout(() => {
        navigate(redirectPath);
      });
    } catch (error) {
      setPasswordError(
        error?.response?.data?.messages?.english ||
          "An error occurred while logging in",
      );
    } finally {
      setLoggingIn(false);
    }
  };

  return (
    <>
      <div>
        <Label htmlFor="username" className="text-md pb-2 float-left">
          Username
        </Label>
        <Input
          type="text"
          name="username"
          value={username}
          placeholder="Enter Username"
          required
          className="w-full border rounded-md px-3 py-2"
          onChange={(e) => {
            if (!scriptRegex.test(e.target.value)) {
              setUsernameError("");
              setUsername(e.target.value);
            }
          }}
        />
        {usernameError && (
          <span className="text-red-500 text-sm mb-2 mt-1">
            {usernameError}
          </span>
        )}
      </div>
      <div>
        <Label htmlFor="password" className="text-md pb-2 float-left">
          Password
        </Label>
        <span className="relative">
          <Input
            type="password"
            name="password"
            placeholder="Enter Password"
            required
            className="w-full border rounded-md px-3 py-2 pr-10"
            onChange={(e) => {
              if (!scriptRegex.test(e.target.value)) {
                passwordRef.current = e.target.value;
                setPasswordError("");
              }
            }}
          />
        </span>
        {passwordError && (
          <span className="text-red-500 text-sm">{passwordError}</span>
        )}
        {loggingIn ? (
          <Button className="w-full bg-btn font-semibold py-2 rounded-md mt-5 flex items-center justify-center gap-2">
            Logging
            <Spinner />
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            className="w-full bg-btn font-semibold py-2 rounded-md mt-5"
          >
            Login
          </Button>
        )}
      </div>
    </>
  );
};

export default AdminLoginForm;
